import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import { clubLifeCategoryMeta } from "../../../constants/clubLifeCategories";
import {
  commentsCountLabel,
  likesCountLabel,
  viewsCountLabel,
} from "../../../utils/ruPlural";
import { commentAuthorName, replyTargetNameDative } from "../../../utils/clubLifeCommentsUtils";
import { personDisplayName } from "../../../utils/clientAppUtils";
import { mediaUrl } from "../../../utils/clientHeroTheme";
import { clientMemberProfileLink } from "../../../utils/socialProfileLink";
import { Avatar } from "../../ui";
import ClubLifeCommentThreads from "./ClubLifeCommentThreads";
import ClubLifePhotoLightbox from "./ClubLifePhotoLightbox";
import ReactionBar from "./ReactionBar";
import s from "../clinic.module.css";

function formatRelative(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "только что";
  if (mins < 60) return `${mins} мин`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ч`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} дн`;
  return formatDateRu(iso);
}

function authorRoleLabel(role) {
  if (role === "admin" || role === "owner") return "Администрация";
  if (role === "trainer") return null;
  return "Клуб";
}

function clientGlobal(modClass, globalName, mode) {
  return mode === "client" || mode === "admin"
    ? `${modClass} ${globalName}`
    : modClass;
}

function isAutoRepostBody(body) {
  const text = String(body || "").trim();
  if (!text) return true;
  return text.startsWith("Поделился записью:");
}

function repostCommentText(body) {
  const text = String(body || "").trim();
  if (!text || isAutoRepostBody(text)) return "";
  return text;
}

function IconComment() {
  return (
    <svg className={s.clubLifeIconSvg} viewBox="0 0 24 24" aria-hidden>
      <path
        d="M21 11.5a8.5 8.5 0 0 1-8.5 8.5c-1.2 0-2.3-.25-3.3-.7L3 21l1.7-5.2A8.5 8.5 0 1 1 21 11.5Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconRepost() {
  return (
    <svg className={s.clubLifeIconSvg} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 7h11a3 3 0 0 1 3 3v1"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="m7 4-3 3 3 3"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M20 17H9a3 3 0 0 1-3-3v-1"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="m17 20 3-3-3-3"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function ClubLifePostCard({
  post,
  mode = "client",
  onLike,
  onComment,
  onRepost,
  canManagePost,
  currentUserId,
  onOpenMenu,
  authorProfileLink,
  onLoadComments,
  comments = [],
  commentsOpen = false,
  commentBusy = false,
}) {
  const [commentText, setCommentText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const commentInputRef = useRef(null);
  const cat = clubLifeCategoryMeta(post.category);
  const media = post.media || [];
  const photos = media.filter((m) => m.type !== "video");
  const authorName = personDisplayName(post.author?.full_name) || "Клуб";
  const likes = post.likes_count ?? 0;
  const commentsCount = post.comments_count ?? 0;
  const views = post.views_count ?? 0;
  const isTrainerPost = post.author?.role === "trainer";
  const roleLabel = authorRoleLabel(post.author?.role);
  const canLike = Boolean(onLike);
  const canComment = Boolean(onComment);
  const canViewComments = Boolean(onLoadComments);
  const canRepost = Boolean(onRepost) && post.scope !== "wall";
  const interactive = canLike || canViewComments || canRepost;

  const profileLink =
    authorProfileLink === false
      ? null
      : authorProfileLink ??
        (mode === "client" ? clientMemberProfileLink(post.author) : null);

  const repostAuthorLink =
    mode === "client" ? clientMemberProfileLink(post.repost_source?.author) : null;
  const repostSource = post.repost_source;
  const repostAuthorName =
    personDisplayName(repostSource?.author?.full_name) || "Клуб";
  const repostComment = repostSource ? repostCommentText(post.body) : "";
  const embedMedia = repostSource?.media?.length
    ? repostSource.media
    : repostSource
      ? media
      : [];
  const embedPhotos = embedMedia.filter((m) => m.type !== "video");
  const embedPreviewPhoto = embedPhotos[0];

  const submitComment = (e) => {
    e.preventDefault();
    if (!commentText.trim() || !onComment) return;
    onComment(post.id, commentText.trim(), () => {
      setCommentText("");
      setReplyTo(null);
    }, replyTo?.id);
  };

  const startReply = (comment) => {
    setReplyTo(comment);
    commentInputRef.current?.focus();
  };

  const cancelReply = () => setReplyTo(null);

  const showMeta =
    !interactive && (likes > 0 || commentsCount > 0 || views > 0);
  const showMenu =
    Boolean(onOpenMenu) &&
    (typeof canManagePost === "function"
      ? canManagePost(post)
      : Boolean(currentUserId) &&
        String(post.author?.id) === String(currentUserId));

  return (
    <article className={clientGlobal(s.clubLifePost, "clubLifePost", mode)}>
      <header className={clientGlobal(s.clubLifePostHeader, "clubLifePostHeader", mode)}>
        {profileLink ? (
          <Link
            to={profileLink}
            className={s.clubLifePostAvatarLink}
            aria-label={`Профиль: ${authorName}`}
          >
            <Avatar name={authorName} src={mediaUrl(post.author?.photo_url)} size="md" />
          </Link>
        ) : (
          <Avatar name={authorName} src={mediaUrl(post.author?.photo_url)} size="md" />
        )}
        <div className={s.clubLifePostAuthorMeta}>
          <div className={s.clubLifePostAuthorTop}>
            {profileLink ? (
              <Link to={profileLink} className={s.clubLifePostAuthorLink}>
                <p className={s.clubLifePostAuthorName}>{authorName}</p>
              </Link>
            ) : (
              <p className={s.clubLifePostAuthorName}>{authorName}</p>
            )}
          </div>
          {!repostSource ? (
            <p className={s.clubLifePostAuthorSub}>
              {isTrainerPost ? (
                <span className={s.clubLifeTrainerBadge}>От тренера</span>
              ) : roleLabel ? (
                <span>{roleLabel}</span>
              ) : null}
              <span className={s.clubLifePostCatChip}>
                {cat.icon} {cat.label}
              </span>
            </p>
          ) : (
            <div className={clientGlobal(s.clubLifeRepostFrom, "clubLifeRepostFrom", mode)}>
              <IconRepost />
              {repostAuthorLink ? (
                <Link to={repostAuthorLink} className={s.clubLifeRepostFromAuthor}>
                  <Avatar
                    name={repostAuthorName}
                    src={mediaUrl(repostSource.author?.photo_url)}
                    size="sm"
                    className={s.clubLifeRepostFromAvatar}
                  />
                  <span>{repostAuthorName}</span>
                </Link>
              ) : (
                <span className={s.clubLifeRepostFromAuthor}>
                  <Avatar
                    name={repostAuthorName}
                    src={mediaUrl(repostSource.author?.photo_url)}
                    size="sm"
                    className={s.clubLifeRepostFromAvatar}
                  />
                  <span>{repostAuthorName}</span>
                </span>
              )}
            </div>
          )}
        </div>
        <div className={clientGlobal(s.clubLifePostHeaderAside, "clubLifePostHeaderAside", mode)}>
          {post.is_pinned ? <span className={s.clubLifePinned}>📌</span> : null}
          <time className={s.clubLifePostTime}>{formatRelative(post.createdAt)}</time>
          {showMenu ? (
            <button
              type="button"
              className={clientGlobal(s.clubLifePostMenuBtn, "clubLifePostMenuBtn", mode)}
              onClick={(e) => onOpenMenu(post, e)}
              aria-label="Действия с записью"
              aria-haspopup="menu"
            >
              ⋯
            </button>
          ) : null}
        </div>
      </header>

      <div className={clientGlobal(s.clubLifePostContent, "clubLifePostContent", mode)}>
        {repostSource ? (
          <>
            {repostComment ? (
              <p className={clientGlobal(s.clubLifePostBody, "clubLifePostBody", mode)}>
                {repostComment}
              </p>
            ) : null}
            <div className={s.clubLifeRepostEmbed}>
              {repostSource.title ? (
                <h4 className={s.clubLifeRepostEmbedTitle}>{repostSource.title}</h4>
              ) : null}
              {repostSource.body ? (
                <p className={s.clubLifeRepostEmbedBody}>{repostSource.body}</p>
              ) : null}
              {embedPreviewPhoto?.url ? (
                <img
                  src={mediaUrl(embedPreviewPhoto.url)}
                  alt=""
                  className={s.clubLifeRepostEmbedImg}
                  loading="lazy"
                />
              ) : null}
            </div>
          </>
        ) : (
          <>
            {post.title ? <h3 className={s.clubLifePostTitle}>{post.title}</h3> : null}
            <p className={clientGlobal(s.clubLifePostBody, "clubLifePostBody", mode)}>
              {post.body}
            </p>
          </>
        )}
      </div>

      {!repostSource && media.length > 0 ? (
        <div className={clientGlobal(s.clubLifePostMediaWrap, "clubLifePostMediaWrap", mode)}>
          <div
            className={`${s.clubLifeMedia} ${
              media.filter((m) => m.type !== "video").length > 1 ? s.clubLifeMediaGrid : ""
            }`}
          >
            {media.map((m) => {
              if (m.type === "video") {
                return (
                  <a
                    key={m.id || m.url}
                    href={m.url}
                    target="_blank"
                    rel="noreferrer"
                    className={s.clubLifeMediaLink}
                  >
                    ▶ Смотреть видео
                  </a>
                );
              }
              const photoIndex = photos.findIndex(
                (p) => (p.id || p.url) === (m.id || m.url),
              );
              return (
                <button
                  key={m.id || m.url}
                  type="button"
                  className={s.clubLifeMediaBtn}
                  onClick={() => setLightboxIndex(photoIndex)}
                  aria-label="Открыть фото"
                >
                  <img
                    src={mediaUrl(m.url)}
                    alt=""
                    className={s.clubLifeMediaImg}
                    loading="lazy"
                  />
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      {lightboxIndex != null && photos.length > 0 ? (
        <ClubLifePhotoLightbox
          images={photos.map((p) => mediaUrl(p.url))}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onChangeIndex={setLightboxIndex}
        />
      ) : null}

      {showMeta ? (
        <div className={clientGlobal(s.clubLifeEngagementBar, "clubLifeEngagementBar", mode)}>
          <span>
            {[likes > 0 ? likesCountLabel(likes) : null, commentsCount > 0 ? commentsCountLabel(commentsCount) : null]
              .filter(Boolean)
              .join(" · ")}
          </span>
          {views > 0 ? <span>{viewsCountLabel(views)}</span> : null}
        </div>
      ) : null}

      {interactive ? (
        <div className={clientGlobal(s.clubLifeSocial, "clubLifeSocial", mode)}>
          {canLike ? (
            <div className={s.clubLifeReactionRow}>
              <ReactionBar
                reactions={post.reactions}
                myReaction={post.my_reaction}
                onReact={(emoji) => onLike(post, emoji)}
                size="md"
                alwaysShowAdd
                className={s.clubLifeReactionBar}
              />
            </div>
          ) : null}
          <div className={clientGlobal(s.clubLifeIconRow, "clubLifeIconRow", mode)}>
            {canViewComments ? (
              <button
                type="button"
                className={`${s.clubLifeIconBtn} ${
                  commentsOpen ? s.clubLifeIconBtnActive : ""
                }`}
                onClick={() => onLoadComments(post.id)}
                aria-label={
                  commentsCount > 0
                    ? `Комментарии: ${commentsCountLabel(commentsCount)}`
                    : "Комментарии"
                }
                aria-expanded={commentsOpen ? "true" : "false"}
              >
                <IconComment />
              </button>
            ) : null}
            {canRepost ? (
              <button
                type="button"
                className={`${s.clubLifeIconBtn} ${s.clubLifeIconBtnRepost}`}
                onClick={() => onRepost(post)}
                aria-label="Репост"
                title="Репост"
              >
                <IconRepost />
              </button>
            ) : null}
          </div>

          {(likes > 0 || commentsCount > 0 || views > 0) && (
            <div className={clientGlobal(s.clubLifeMetrics, "clubLifeMetrics", mode)}>
              <div className={s.clubLifeMetricsPrimary}>
                {likes > 0 ? (
                  <span className={s.clubLifeLikesLine}>{likesCountLabel(likes)}</span>
                ) : null}
                {commentsCount > 0 ? (
                  <span className={s.clubLifeCommentsLine}>
                    {commentsCountLabel(commentsCount)}
                  </span>
                ) : null}
              </div>
              {views > 0 ? (
                <span className={s.clubLifeViewsLine}>{viewsCountLabel(views)}</span>
              ) : null}
            </div>
          )}

          {commentsCount > 0 && !commentsOpen && canViewComments ? (
            <button
              type="button"
              className={clientGlobal(s.clubLifeCommentsPreview, "clubLifeCommentsPreview", mode)}
              onClick={() => onLoadComments(post.id)}
            >
              {commentsCount === 1
                ? "Посмотреть комментарий"
                : `Посмотреть все комментарии (${commentsCount})`}
            </button>
          ) : null}
        </div>
      ) : null}

      {commentsOpen ? (
        <div className={clientGlobal(s.clubLifeComments, "clubLifeComments", mode)}>
          <ClubLifeCommentThreads
            comments={comments}
            mode={mode}
            onReply={canComment ? startReply : null}
            linkProfiles={mode === "client"}
          />
          {canComment ? (
            <form onSubmit={submitComment} className={clientGlobal(s.clubLifeCommentForm, "clubLifeCommentForm", mode)}>
              {replyTo ? (
                <div className={s.clubLifeReplyBanner}>
                  <span>
                    Ответ{" "}
                    <strong>
                      {replyTargetNameDative(replyTo.author) ||
                        commentAuthorName(replyTo.author)}
                    </strong>
                  </span>
                  <button
                    type="button"
                    className={s.clubLifeReplyCancel}
                    onClick={cancelReply}
                    aria-label="Отменить ответ"
                  >
                    ×
                  </button>
                </div>
              ) : null}
              <input
                ref={commentInputRef}
                type="text"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={
                  replyTo
                    ? `Ответ ${replyTargetNameDative(replyTo.author) || commentAuthorName(replyTo.author)}…`
                    : "Добавить комментарий…"
                }
                className={s.clubLifeCommentInput}
              />
              {commentText.trim() ? (
                <button
                  type="submit"
                  className={s.clubLifeCommentSubmit}
                  disabled={commentBusy}
                >
                  {commentBusy ? "…" : "Отправить"}
                </button>
              ) : null}
            </form>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}
